import { useState, useEffect } from 'react';
import { getSpotRating, getUserRating, upsertRating } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import styles from './StarRating.module.css';

export default function StarRating({ spotId, showCount = true, readOnly = false }) {
  const { user } = useAuth();
  const [average, setAverage] = useState(null);
  const [totalRatings, setTotalRatings] = useState(0);
  const [userRating, setUserRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadRatings();
  }, [spotId]);

  const loadRatings = async () => {
    const { data: ratingData } = await getSpotRating(spotId);
    if (ratingData) {
      setAverage(ratingData.average_rating);
      setTotalRatings(ratingData.total_ratings);
    }
    if (user) {
      const { data: userRatingData } = await getUserRating(spotId);
      if (userRatingData) setUserRating(userRatingData.rating);
    }
  };

  const handleRate = async (star) => {
    if (!user) {
      toast.error('Sign in to rate this spot');
      return;
    }
    setLoading(true);
    const { error } = await upsertRating(spotId, star);
    if (error) {
      toast.error(error.message);
    } else {
      setUserRating(star);
      toast.success(userRating ? 'Rating updated!' : 'Thanks for rating!');
      await loadRatings();
    }
    setLoading(false);
  };

  const display = hovered || userRating;

  return (
    <div className={styles.wrap}>
      <div className={styles.stars}>
        {[1, 2, 3, 4, 5].map(star => (
  <button
    key={star}
    className={styles.star}
    onMouseEnter={() => !readOnly && setHovered(star)}
    onMouseLeave={() => !readOnly && setHovered(0)}
    onClick={() => !readOnly && handleRate(star)}
    disabled={loading || readOnly}
    style={{ cursor: readOnly ? 'default' : 'pointer' }}
  >
    <svg
      width={readOnly ? 14 : 24}
      height={readOnly ? 14 : 24}
      viewBox="0 0 24 24"
      fill={star <= (readOnly ? average : display) ? '#E8621A' : 'none'}
      stroke={star <= (readOnly ? average : display) ? '#E8621A' : 'var(--muted)'}
      strokeWidth="1.5"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  </button>
))}
      </div>

      <div className={styles.info}>
        {average ? (
          <>
            <span className={styles.average}>{average}</span>
            {showCount && (
              <span className={styles.count}>
                ({totalRatings} {totalRatings === 1 ? 'rating' : 'ratings'})
              </span>
            )}
          </>
        ) : (
          <span className={styles.count}>No ratings yet</span>
        )}
      </div>

      {user && userRating > 0 && (
        <span className={styles.yourRating}>Your rating: {userRating} star{userRating > 1 ? 's' : ''}</span>
      )}
    </div>
  );
}